import React, { useState, useEffect, useCallback } from 'react';
import { FaGamepad, FaUsers, FaCoins, FaCheckCircle } from 'react-icons/fa';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import { useAppSelector } from '../../store/hooks';
import axiosInstance from '../../services/axiosConfig';
import './AdminComponents.css';

interface P2PGamesSummaryData {
  totalGames: number;
  activeGames: number;
  totalSessions: number;
  activeSessions: number;
  completedSessions: number;
  totalStake: number;
  totalPayout: number;
}

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Helper functions for cache management
const getCachedData = (key: string) => {
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      if (now - timestamp < CACHE_DURATION) {
        console.log(`P2PGamesSummary: Using cached data for ${key}, age: ${Math.round((now - timestamp) / 1000)}s`);
        return data;
      } else {
        console.log(`P2PGamesSummary: Cache expired for ${key}, age: ${Math.round((now - timestamp) / 1000)}s`);
        sessionStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.error(`Failed to get cached data for ${key}:`, error);
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
    console.log(`P2PGamesSummary: Cached data for ${key}`);
  } catch (error) {
    console.error(`Failed to cache data for ${key}:`, error);
  }
};

const P2PGamesSummary: React.FC = () => {
  const [summary, setSummary] = useState<P2PGamesSummaryData | null>(() => {
    const cached = getCachedData('p2p-games-summary');
    return cached || null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const adminApi = useAdminApiService();
  const adminUser = useAppSelector(state => state.auth.adminUser);

  const hasPermission = adminUser?.permissions?.includes('VIEW_ADMIN_P2PGAME') || false;

  const loadSummary = useCallback(async () => {
    if (!hasPermission) {
      setLoading(false);
      return;
    }

    // Check cache first
    const cached = getCachedData('p2p-games-summary');
    if (cached) {
      console.log('P2PGamesSummary: Using cached data, skipping API call');
      setSummary(cached);
      setLoading(false);
      return;
    }

    console.log('P2PGamesSummary: Making API call');
    setLoading(true);
    setError(null);

    try {
      // Fetch games and sessions to calculate summary
      const [gamesResponse, sessionsResponse, statsResponse] = await Promise.all([
        axiosInstance.get('/api/admin/p2p-games/list'),
        axiosInstance.get('/api/admin/p2p-games/sessions?status=ALL'),
        axiosInstance.get('/api/admin/p2p-games/statistics').catch(() => null)
      ]);

      if (gamesResponse.data?.status === 'success') {
        const games = gamesResponse.data.data || [];
        const activeGames = games.filter((g: any) => g.isEnabled).length;
        const totalGames = games.length;

        let totalSessions = 0;
        let activeSessions = 0;
        let completedSessions = 0;
        let totalStake = 0;
        let totalPayout = 0;

        if (sessionsResponse.data?.status === 'success') {
          const sessions = sessionsResponse.data.data || [];
          totalSessions = sessions.length;
          activeSessions = sessions.filter((s: any) => s.status === 'IN_PROGRESS' || s.status === 'WAITING').length;
          completedSessions = sessions.filter((s: any) => s.status === 'COMPLETED').length;
          
          // Calculate total stake and payout from sessions
          sessions.forEach((session: any) => {
            if (session.stake) totalStake += parseFloat(session.stake) || 0;
            if (session.payout) totalPayout += parseFloat(session.payout) || 0;
          });
        }

        // Use statistics if available
        if (statsResponse?.data?.status === 'success' && statsResponse.data.data) {
          const stats = statsResponse.data.data;
          totalStake = stats.totalStake || totalStake;
          totalPayout = stats.totalPayout || totalPayout;
        }

        const summaryData: P2PGamesSummaryData = {
          totalGames,
          activeGames,
          totalSessions,
          activeSessions,
          completedSessions,
          totalStake,
          totalPayout
        };

        setSummary(summaryData);
        
        // Cache the summary
        setCachedData('p2p-games-summary', summaryData);
      } else {
        throw new Error('Failed to load P2P games data');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load P2P games summary');
      console.error('P2P games summary error:', err);
    } finally {
      setLoading(false);
    }
  }, [hasPermission]);

  useEffect(() => {
    if (hasPermission) {
      loadSummary();
    } else {
      setLoading(false);
    }
  }, [hasPermission, loadSummary]);

  if (!hasPermission) {
    return null;
  }

  if (loading) {
    return (
      <div className="p2p-games-summary-card stat-card">
        <div className="stat-card-header">
          <h3>
            <FaGamepad style={{ color: '#e7038e' }} />
            <span>P2P Games Summary</span>
          </h3>
        </div>
        <div className="stat-card-content">
          <div className="loading-spinner-small"></div>
          <p>Loading P2P games statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p2p-games-summary-card stat-card">
        <div className="stat-card-header">
          <h3>
            <FaGamepad style={{ color: '#e7038e' }} />
            <span>P2P Games Summary</span>
          </h3>
        </div>
        <div className="stat-card-content">
          <p className="error-text">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p2p-games-summary-card stat-card">
      <div className="stat-card-header">
        <h3>
          <FaGamepad style={{ color: '#e7038e' }} />
          <span>P2P Games Summary</span>
        </h3>
      </div>
      <div className="stat-card-content">
        <div className="summary-stats-grid">
          <div className="summary-stat-item">
            <div className="summary-stat-icon">
              <FaGamepad style={{ color: '#e7038e' }} />
            </div>
            <div className="summary-stat-details">
              <div className="summary-stat-label">Total Games</div>
              <div className="summary-stat-value">{summary?.totalGames || 0}</div>
              <div className="summary-stat-subtext">{summary?.activeGames || 0} active</div>
            </div>
          </div>
          <div className="summary-stat-item">
            <div className="summary-stat-icon">
              <FaUsers style={{ color: '#086afb' }} />
            </div>
            <div className="summary-stat-details">
              <div className="summary-stat-label">Total Sessions</div>
              <div className="summary-stat-value">{summary?.totalSessions || 0}</div>
              <div className="summary-stat-subtext">{summary?.activeSessions || 0} active</div>
            </div>
          </div>
          <div className="summary-stat-item">
            <div className="summary-stat-icon">
              <FaCheckCircle style={{ color: '#0dca9d' }} />
            </div>
            <div className="summary-stat-details">
              <div className="summary-stat-label">Completed</div>
              <div className="summary-stat-value">{summary?.completedSessions || 0}</div>
            </div>
          </div>
          <div className="summary-stat-item">
            <div className="summary-stat-icon">
              <FaCoins style={{ color: '#fbb445' }} />
            </div>
            <div className="summary-stat-details">
              <div className="summary-stat-label">Total Stake</div>
              <div className="summary-stat-value">${(summary?.totalStake || 0).toLocaleString()}</div>
            </div>
          </div>
          <div className="summary-stat-item">
            <div className="summary-stat-icon">
              <FaCoins style={{ color: '#e7038e' }} />
            </div>
            <div className="summary-stat-details">
              <div className="summary-stat-label">Total Payout</div>
              <div className="summary-stat-value">${(summary?.totalPayout || 0).toLocaleString()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default P2PGamesSummary;

